import { useAtomValue } from 'jotai';
import { useMemo } from 'react';

import {
  SelectionsDeviceGroup,
  SidebarAccordionItem,
} from '@/features/trends/components/selections-sidebar/selections.types';
import SelectionState from '@/features/trends/components/selections-sidebar/state/selections.state';
import { useAttributeSelection } from '@/features/trends/state/hooks/use-attribute-selection';

interface UseSelectAllParams {
  selectionGroups: SidebarAccordionItem<SelectionsDeviceGroup>[];
}

type CheckedState = boolean | 'indeterminate';

interface UseSelectAllResult {
  checkedState: CheckedState;
  handleSelectAllChange: (checked: boolean) => void;
  visibleAttributesCount: number;
}

/**
 * Hook for managing "select all" functionality for visible attributes.
 * Calculates selection state and provides a handler for selecting/deselecting all visible attributes.
 *
 * @param selectionGroups - The grouped devices to extract visible attributes from
 * @returns Selection state and handler for the select all checkbox
 */
export const useSelectAll = ({ selectionGroups }: UseSelectAllParams): UseSelectAllResult => {
  const getVisibleAttributes = useAtomValue(SelectionState.visibleAttributes);
  const { addAttributeAction, removeAttributeAction, hasAttribute } = useAttributeSelection();

  // Get all visible attributes
  const visibleAttributes = useMemo(
    () => getVisibleAttributes(selectionGroups),
    [getVisibleAttributes, selectionGroups]
  );

  // Calculate checked state: true (all selected), false (none selected), or 'indeterminate' (some selected)
  const checkedState = useMemo<CheckedState>(() => {
    if (visibleAttributes.length === 0) return false;

    const selectedCount = visibleAttributes.filter((attr) => hasAttribute(attr)).length;

    if (selectedCount === 0) return false;
    if (selectedCount === visibleAttributes.length) return true;
    return 'indeterminate';
  }, [visibleAttributes, hasAttribute]);

  const handleSelectAllChange = (checked: boolean) => {
    if (checked) {
      addAttributeAction(visibleAttributes);
    } else {
      removeAttributeAction(visibleAttributes);
    }
  };

  return {
    checkedState,
    handleSelectAllChange,
    visibleAttributesCount: visibleAttributes.length,
  };
};
